import { useCallback } from "react";
import useSWR, { mutate } from "swr";
import useSWRMutation from "swr/mutation";
import { Paginated, PaginationQuery } from "../types";
import { buildUrl, mutateCache, useApi } from "./api";
import { CustomError } from "./errors";
import { FullProject, SearchAdminProject, SearchProject, UpsertProjectBody } from "./types/projects";

export const useProjectsApi = () => {
  const api = useApi();

  // ADMIN ENDPOINTS - START

  // Get a single project
  const getProjectById = (signal: AbortSignal, isAdmin?: boolean) => {
    return useSWRMutation<FullProject, CustomError, string, string>(
      `${isAdmin ? "/admin" : ""}/projects`,
      (url, { arg: id }) => api.get<FullProject>({ url: `${url}/${id}`, signal }),
    );
  };

  // Get a paginated list of projects
  const useAdminProjects = (query: PaginationQuery, signal: AbortSignal, enabled = true) => {
    const url = buildUrl("/admin/projects", query);
    return useSWR<Paginated<SearchAdminProject>, CustomError, string | null>(enabled ? url : null, () =>
      api.get<Paginated<SearchAdminProject>>({ url, signal }),
    );
  };

  // Insert a new project
  const insertAdminProject = useCallback(async (body: UpsertProjectBody): Promise<SearchAdminProject> => {
    const url = `/admin/projects`;
    const item = await api.post<SearchAdminProject, UpsertProjectBody>(url, body);
    mutateCache(["/admin/projects", "/projects"]);
    return item;
  }, []);

  // Update an existing project
  const updateAdminProject = useCallback(async (itemId: string, body: UpsertProjectBody): Promise<void> => {
    const url = `/admin/projects/${itemId}`;
    await api.put<UpsertProjectBody>(url, body);
    mutateCache(["/admin/projects", "/projects"], url);
  }, []);

  // Delete a project
  const deleteAdminProject = useCallback(async (projectId: string): Promise<void> => {
    await api.del(`/admin/projects/${projectId}`).then(() => {
      mutateCache(["/admin/projects", `/admin/projects/${projectId}`, "/projects"]);
    });
  }, []);

  // ADMIN ENDPOINTS - END

  const getMyProjects = useCallback(
    (signal: AbortSignal) => {
      const url = "/projects";
      return useSWRMutation<SearchProject[], CustomError, string>(url, () =>
        api.get<Paginated<SearchProject>>({ url, signal }).then((x) => x.items),
      );
    },
    [api],
  );

  const useMyProjects = useCallback(
    (query: PaginationQuery, signal: AbortSignal, enabled = true) => {
      const url = buildUrl("/projects", query);
      return useSWR<Paginated<SearchProject>, CustomError, string | null>(enabled ? url : null, () =>
        api.get<Paginated<SearchProject>>({ url, signal }),
      );
    },
    [api],
  );

  const mutateMyProjects = useCallback(
    (query: PaginationQuery, optimisticData: Paginated<SearchProject>) => {
      const url = buildUrl("/projects", query);
      mutate(url, optimisticData, { revalidate: false, optimisticData: true });
    },
    [api],
  );

  // Insert a new project
  const insertMyProject = useCallback(async (body: UpsertProjectBody): Promise<SearchAdminProject> => {
    const url = `/projects`;
    const item = await api.post<SearchAdminProject, UpsertProjectBody>(url, body);
    mutateCache(["/projects", "/admin/projects"]);
    return item;
  }, []);

  // Update an existing project
  const updateMyProject = useCallback(async (itemId: string, body: UpsertProjectBody): Promise<void> => {
    const url = `/projects/${itemId}`;
    await api.put<UpsertProjectBody>(url, body);
    mutateCache(["/projects", "/admin/projects"], url);
  }, []);

  const deleteMyProject = useCallback(async (projectId: string): Promise<void> => {
    await api.del(`/projects/${projectId}`).then(() => {
      mutateCache(["/projects", "/admin/projects", `/projects/${projectId}`]);
    });
  }, []);

  const updateProjectStatus = useCallback(async (projectId: string, status: string, isAdmin?: boolean): Promise<void> => {
    const url = `${isAdmin ? "/admin" : ""}/projects/${projectId}/status`;
    await api.put<{ status: string }>(url, { status });
    mutateCache([`/projects/${projectId}`, `/admin/projects/${projectId}`]);
  }, [api]);

  return {
    getProjectById,

    useAdminProjects,
    insertAdminProject,
    updateAdminProject,
    deleteAdminProject,

    getMyProjects,
    useMyProjects,
    // getMyProjectById,
    insertMyProject,
    updateMyProject,
    deleteMyProject,
    mutateMyProjects,
    updateProjectStatus,
  };
};
